import { useCallback, useEffect, useRef, useState } from "react";
import dsp from "../../template/images/LogoDSP.svg";
import "../layout/accessibility.css";
import "./EventsLanding.css";
export default function DspHeader() {
  // Accessibility state and ref
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const accessibilityRef = useRef(null);

  const [activeAccessibility, setActiveAccessibility] = useState(() => {
    const savedSettings = localStorage.getItem("accessibilitySettings");
    return savedSettings
      ? JSON.parse(savedSettings)
      : {
          contrast: false,
          spacing: false,
          cursor: false,
          lineHeight: false,
          hideImage: false,
          highlightLinks: false,
          saturation: false,
          invert: false,
        };
  });

  const toggleAccessibility = () => {
    setAccessibilityOpen((prev) => !prev);
  };

  const toggleBodyClass = (key, className) => {
    const isActive = activeAccessibility[key];

    if (isActive) {
      document.body.classList.remove(className);
    } else {
      document.body.classList.add(className);
    }

    const newSettings = {
      ...activeAccessibility,
      [key]: !isActive,
    };

    setActiveAccessibility(newSettings);
    localStorage.setItem("accessibilitySettings", JSON.stringify(newSettings));
  };

  const setBodyClass = (key, className, shouldAdd) => {
    console.log(
      `setBodyClass called: key=${key}, className=${className}, shouldAdd=${shouldAdd}`,
    );

    if (shouldAdd) {
      document.body.classList.add(className);
      console.log(`Added class "${className}" to body`);
    } else {
      document.body.classList.remove(className);
      console.log(`Removed class "${className}" from body`);
    }

    const newSettings = {
      ...activeAccessibility,
      [key]: shouldAdd,
    };

    setActiveAccessibility(newSettings);
    localStorage.setItem("accessibilitySettings", JSON.stringify(newSettings));
    console.log("Updated settings:", newSettings);
    console.log("Body classes:", document.body.className);
  };

  const handleClickOutside = useCallback((event) => {
    if (
      accessibilityRef.current &&
      !accessibilityRef.current.contains(event.target)
    ) {
      setAccessibilityOpen(false);
    }
  }, []);

  useEffect(() => {
    if (accessibilityOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [accessibilityOpen, handleClickOutside]);

  // Apply saved accessibility settings on mount
  useEffect(() => {
    console.log(
      "Applying saved accessibility settings on mount:",
      activeAccessibility,
    );
    Object.entries(activeAccessibility).forEach(([key, isActive]) => {
      const className = {
        contrast: "contrast",
        spacing: "addLetterSpacing",
        cursor: "big_cursor",
        lineHeight: "line_Height",
        hideImage: "hide_Image",
        highlightLinks: "highlight_Links",
        saturation: "saturation",
        invert: "invert",
      }[key];

      if (isActive && className) {
        document.body.classList.add(className);
        console.log(`Applied saved setting: ${key} -> ${className}`);
      }
    });
    console.log("Final body classes after mount:", document.body.className);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <header className="events-header">
      <div className="container-lg">
        <div className="header-content">
          <div className="logo-section">
            <div className="dsp-logo">
              <img src={dsp} alt="log" />
            </div>
          </div>
          <div className="header-actions">
            <div
              className="accessibility-wrapper"
              ref={accessibilityRef}
              style={{ position: "relative" }}
            >
              <button
                className="accessibility-btn"
                aria-label="Accessibility"
                onClick={toggleAccessibility}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                >
                  <path
                    d="M12.3629 0C15.6418 0 18.7864 1.30252 21.1048 3.62102C23.4233 5.93952 24.7259 9.08408 24.7259 12.3629C24.7259 15.6418 23.4233 18.7864 21.1048 21.1048C18.7864 23.4233 15.6418 24.7259 12.3629 24.7259C9.08408 24.7259 5.93952 23.4233 3.62102 21.1048C1.30252 18.7864 0 15.6418 0 12.3629C0 9.08408 1.30252 5.93952 3.62102 3.62102C5.93952 1.30252 9.08408 0 12.3629 0ZM12.3629 1.77333C10.9462 1.73126 9.53545 1.97397 8.21423 2.48708C6.89302 3.00019 5.68823 3.77327 4.67125 4.7605C3.65426 5.74773 2.84577 6.92904 2.29367 8.23445C1.74157 9.53985 1.45709 10.9428 1.45709 12.3601C1.45709 13.7775 1.74157 15.1804 2.29367 16.4858C2.84577 17.7912 3.65426 18.9725 4.67125 19.9598C5.68823 20.947 6.89302 21.7201 8.21423 22.2332C9.53545 22.7463 10.9462 22.989 12.3629 22.9469C15.1291 22.8828 17.7603 21.7388 19.6938 19.7597C21.6273 17.7805 22.7095 15.1233 22.709 12.3564C22.7085 9.58958 21.6253 6.93276 19.6912 4.95427C17.757 2.97579 15.1253 1.83273 12.3592 1.7696M17.8715 8.27307C17.9912 8.23635 18.1171 8.22412 18.2417 8.2371C18.3663 8.25008 18.487 8.288 18.5966 8.34862C18.7063 8.40923 18.8026 8.49128 18.8798 8.58989C18.957 8.6885 19.0136 8.80165 19.0462 8.9226C19.0788 9.04355 19.0867 9.16982 19.0695 9.29389C19.0522 9.41795 19.0102 9.53728 18.9459 9.64476C18.8815 9.75224 18.7962 9.84567 18.695 9.91948C18.5938 9.99329 18.4788 10.046 18.3568 10.0744L14.6851 11.0581V13.8003C14.6851 14.924 16.3277 20.1973 16.3277 20.1973C16.3596 20.3157 16.3678 20.4393 16.352 20.5608C16.3361 20.6824 16.2964 20.7997 16.2353 20.9059C16.1117 21.1205 15.9079 21.2772 15.6688 21.3416C15.4297 21.406 15.1748 21.3727 14.9602 21.2491C14.7456 21.1255 14.5889 20.9218 14.5245 20.6827C14.5245 20.6827 13.1469 15.3981 13.0499 15.1629C12.9603 14.9259 12.6429 14.9259 12.6429 14.9259H12.0699C12.0587 14.9259 11.7451 14.9296 11.6611 15.1629C11.5584 15.4037 10.1901 20.6509 10.1808 20.6864C10.1491 20.8048 10.0943 20.9158 10.0197 21.013C9.94502 21.1103 9.85197 21.1919 9.7458 21.2531C9.63963 21.3144 9.52243 21.3542 9.4009 21.3701C9.27936 21.3861 9.15587 21.378 9.03747 21.3463C8.91907 21.3145 8.80808 21.2598 8.71084 21.1851C8.6136 21.1105 8.53202 21.0174 8.47074 20.9113C8.40947 20.8051 8.36971 20.6879 8.35372 20.5664C8.33774 20.4448 8.34586 20.3213 8.3776 20.2029C8.3888 20.1712 10.0221 14.9203 10.0221 13.8003V11.0563L6.36533 10.0781C6.13397 10.0072 5.93919 9.84919 5.8221 9.63741C5.705 9.42563 5.67474 9.17664 5.7377 8.94298C5.80065 8.70932 5.9519 8.50924 6.15954 8.38496C6.36719 8.26067 6.61498 8.22189 6.85067 8.2768C6.85067 8.2768 9.93814 9.32587 11.1421 9.32587H13.5875C14.7859 9.32587 17.8472 8.28613 17.8752 8.2768M12.3629 3.72027C12.9199 3.72027 13.454 3.94152 13.8479 4.33534C14.2417 4.72917 14.4629 5.26331 14.4629 5.82027C14.4629 6.37722 14.2417 6.91137 13.8479 7.30519C13.454 7.69902 12.9199 7.92027 12.3629 7.92027C11.806 7.92027 11.2718 7.69902 10.878 7.30519C10.4842 6.91137 10.2629 6.37722 10.2629 5.82027C10.2629 5.26331 10.4842 4.72917 10.878 4.33534C11.2718 3.94152 11.806 3.72027 12.3629 3.72027Z"
                    fill="#0CF9E0"
                  />
                </svg>
              </button>

              {accessibilityOpen && (
                <div className="accessibility-dropdown">
                  <h5 className="accessibility-heading">Accessibility Tools</h5>
                  <div className="acess-item">
                    <h4>Color contrast</h4>
                    <div className="access-item-row">
                      <button
                        className={activeAccessibility.contrast ? "active" : ""}
                        onClick={() =>
                          setBodyClass("contrast", "contrast", true)
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M480-288.46 531.54-340H620v-88.46L671.54-480 620-531.54V-620h-88.46L480-671.54 428.46-620H340v88.46L288.46-480 340-428.46V-340h88.46L480-288.46Zm0-81.54v-220q45.77 0 77.88 32.12Q590-525.77 590-480q0 45.77-32.12 77.88Q525.77-370 480-370ZM172.31-180Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31v455.38Q860-222 839-201q-21 21-51.31 21H172.31Zm0-60h615.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H172.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM160-240v-480 480Z"></path>
                        </svg>
                        <span>High Contrast</span>
                      </button>
                      <button
                        className={
                          !activeAccessibility.contrast ? "active" : ""
                        }
                        onClick={() =>
                          setBodyClass("contrast", "contrast", false)
                        }
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 16.7885L13.2885 15.5H15.5V13.2885L16.7885 12L15.5 10.7115V8.5H13.2885L12 7.2115L10.7115 8.5H8.5V10.7115L7.2115 12L8.5 13.2885V15.5H10.7115L12 16.7885ZM4.30775 19.5C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H19.6923C20.1974 4.5 20.625 4.675 20.975 5.025C21.325 5.375 21.5 5.80258 21.5 6.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775ZM4.30775 18H19.6923C19.7693 18 19.8398 17.9679 19.9038 17.9038C19.9679 17.8398 20 17.7693 20 17.6923V6.30775C20 6.23075 19.9679 6.16025 19.9038 6.09625C19.8398 6.03208 19.7693 6 19.6923 6H4.30775C4.23075 6 4.16025 6.03208 4.09625 6.09625C4.03208 6.16025 4 6.23075 4 6.30775V17.6923C4 17.7693 4.03208 17.8398 4.09625 17.9038C4.16025 17.9679 4.23075 18 4.30775 18Z"></path>
                          <path
                            d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
                            fill="white"
                          ></path>
                        </svg>
                        <span>Normal Contrast</span>
                      </button>

                      <button
                        className={
                          activeAccessibility.highlightLinks ? "active" : ""
                        }
                        onClick={() =>
                          toggleBodyClass("highlightLinks", "highlight_Links")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M432.31-298.46H281.54q-75.34 0-128.44-53.1Q100-404.65 100-479.98q0-75.33 53.1-128.44 53.1-53.12 128.44-53.12h150.77v60H281.54q-50.39 0-85.96 35.58Q160-530.38 160-480q0 50.38 35.58 85.96 35.57 35.58 85.96 35.58h150.77v60ZM330-450v-60h300v60H330Zm197.69 151.54v-60h150.77q50.39 0 85.96-35.58Q800-429.62 800-480q0-50.38-35.58-85.96-35.57-35.58-85.96-35.58H527.69v-60h150.77q75.34 0 128.44 53.1Q860-555.35 860-480.02q0 75.33-53.1 128.44-53.1 53.12-128.44 53.12H527.69Z"></path>
                        </svg>
                        <span>Highlight links</span>
                      </button>
                      <button
                        className={
                          activeAccessibility.saturation ? "active" : ""
                        }
                        onClick={() =>
                          toggleBodyClass("saturation", "saturation")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M480-140q-124 0-202-78.5T200-420q0-85 39.5-148T339-664q60-33 121-66.5T580-810q3-2 6-2.5t6 .5q3 1 5 3t2 5v54q0 46 14 81.5T648-600v180q0 24.75 17.625 42.375T708-360h42q9 0 14.5 6.5T770-338q-11 109-89 183.5T480-80Zm0-60q80 0 140-51.5T699-320h-51q-50 0-84.5-34.5T529-439v-211q-26 20-44.5 50T461-532q-14 25-30 49.5T400-442q-15 17-22.5 37.5T370-362q0 50 35 86t75 36Zm-1-200Z"></path>
                        </svg>
                        <span>Saturation</span>
                      </button>
                      <button
                        className={activeAccessibility.invert ? "active" : ""}
                        onClick={() => toggleBodyClass("invert", "invert")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M480-100q-78.85 0-148.21-29.92-69.37-29.93-120.65-81.21-51.29-51.28-81.21-120.65Q100-401.15 100-480q0-78.85 29.92-148.21 29.93-69.37 81.21-120.65 51.28-51.29 120.65-81.21Q401.15-860 480-860q78.85 0 148.21 29.92 69.37 29.93 120.65 81.21 51.29 51.28 81.21 120.65Q860-558.85 860-480q0 78.85-29.92 148.21-29.93 69.37-81.21 120.65-51.28 51.29-120.65 81.21Q558.85-100 480-100Zm0-60v-640q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160Z"></path>
                        </svg>
                        <span>Invert colors</span>
                      </button>
                    </div>
                  </div>

                  <div className="acess-item">
                    <h4>Spacing</h4>
                    <div className="access-item-row">
                      <button
                        className={activeAccessibility.spacing ? "active" : ""}
                        onClick={() =>
                          toggleBodyClass("spacing", "addLetterSpacing")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M380-160v-559.38L242.15-581.54 200-623.69 400-823.69l200 200-42.15 42.15L420-719.38V-160h-40Zm180 0v-200h40v200h-40Zm0-319.38v-40h40v40h-40Zm0-160.62v-200h40v200h-40Z"></path>
                        </svg>
                        <span>Increase spacing</span>
                      </button>
                      <button
                        className={
                          activeAccessibility.lineHeight ? "active" : ""
                        }
                        onClick={() =>
                          toggleBodyClass("lineHeight", "line_Height")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M140-200v-40h280v40H140Zm0-160v-40h440v40H140Zm0-160v-40h440v40H140Zm0-160v-40h440v40H140Zm520 520v-251.54L598.46-350 560-388.46 710-538.46l150 150L821.54-350 760-411.54V-160h-100Zm0-400v-240h100v240H660Z"></path>
                        </svg>
                        <span>Line height</span>
                      </button>
                    </div>
                  </div>

                  <div className="acess-item">
                    <h4>Visual</h4>
                    <div className="access-item-row">
                      <button
                        className={activeAccessibility.cursor ? "active" : ""}
                        onClick={() => toggleBodyClass("cursor", "big_cursor")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M480.22-343.08q-92.84 0-158.03-64.97Q257-472.01 257-564.85q0-92.84 65.01-158.03 65-65.2 157.84-65.2 92.84 0 158.03 65.02 65.2 65.01 65.2 157.85 0 92.29-65.02 157.25-65.01 64.96-158.06 64.96l.22-.08ZM200-182.54v-40h560v40H200Z"></path>
                        </svg>
                        <span>Big cursor</span>
                      </button>
                      <button
                        className={
                          activeAccessibility.hideImage ? "active" : ""
                        }
                        onClick={() =>
                          toggleBodyClass("hideImage", "hide_Image")
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          viewBox="0 -960 960 960"
                          width="24px"
                        >
                          <path d="M792.92-93.85 677.38-209.39H172.31q-27.01 0-45.66-18.65Q108-246.69 108-273.7v-412.6q0-27.7 18.16-46.35 18.15-18.66 44.84-18.66h21.54l36.15 36.16H175l-93.15-93.16L110-836.46l711.08 711.07-28.16 28.16ZM823.39-316l-40-40v-331.69q0-5.39-3.47-8.85-3.46-3.46-8.84-3.46H438.85l-60-60h408.84q27.01 0 45.66 18.65Q852-722.7 852-695.69v412.6q0 9.24-2.69 17.7-2.7 8.47-8.08 14.85l-17.84-65.46ZM269.23-290h348.15l-102-102-49.61 66-93.85-123.85L269.23-290Zm221.08-266.31ZM404-384.77Z"></path>
                        </svg>
                        <span>Hide images</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button className="my-events-btn">My Events</button>
          </div>
        </div>
      </div>
    </header>
  );
}
